// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Covers: onboarding wizard, OAuth consent + callback, magic-link landing,
// greenfield signup, the "Add HQ to Slack" landing, 404, and Help & docs.
import type { LocaleStrings } from './base';

export const onboardingAuthStrings: LocaleStrings = {
  en: {
    // ── web-ui OnboardingView.vue + OnboardingStepper.vue ──
    'onboarding.stepper_aria':         'Onboarding progress',
    'onboarding.step.welcome':         'Welcome',
    'onboarding.step.url':             'Workspace URL',
    'onboarding.step.channels':        'Channels',
    'onboarding.step.domain':          'Domain',
    'onboarding.step.done':            'Done',
    'onboarding.switch_to':            'Switch to {name}',
    'onboarding.theme_light':          'Light',
    'onboarding.theme_dark':           'Dark',
    'onboarding.theme_light_title':    'Light theme',
    'onboarding.theme_dark_title':     'Dark theme',
    'onboarding.sign_out':             'Sign out',
    'onboarding.loading_workspace':    'Loading your workspace...',
    'onboarding.continue':             'Continue',
    'onboarding.err.join_channel':     'Could not add HQ to a channel.',

    'onboarding.welcome.title':        'Welcome to HQ',
    'onboarding.welcome.lede':         "Let's get your workspace set up. It only takes a minute - we'll connect a few channels, learn your company, and you're ready to put your agents to work.",
    'onboarding.welcome.cta':          'Get started',

    'onboarding.url.title':            'Choose your workspace URL',
    'onboarding.url.lede':             "This is your workspace's handle - it's in your web address and your agents' email address. We picked one from your workspace name; change it if you'd like. You can rename it later in settings.",

    'onboarding.channels.title':       'Add HQ to your channels',
    'onboarding.channels.lede':        'Pick the channels where HQ should listen and help. You can always add more later from workspace settings.',
    'onboarding.channels.loading':     'Loading channels...',
    'onboarding.channels.connected':   'Connected',
    'onboarding.channels.add':         'Add channels',
    'onboarding.channels.empty_title': 'No channels to add',
    'onboarding.channels.empty_body':  'HQ is already in every public channel it can self-join.',
    'onboarding.channels.private_before':'Private channels need an invite - run',
    'onboarding.channels.private_after': 'in Slack.',
    'onboarding.channels.skip':        'Skip for now',
    'onboarding.channels.join_n':      'Join {count} selected',

    'onboarding.domain.title':         'Confirm your company domain',
    'onboarding.domain.lede':          'Confirm your company domain so we can build your workspace profile. We use it to learn what your company does and tailor your agents.',
    'onboarding.domain.aria':          'Company domain',
    'onboarding.domain.checking':      'Checking...',
    'onboarding.domain.ok_after':      'looks good',
    'onboarding.domain.unresolved':    "We couldn't find that domain - check the spelling",
    'onboarding.domain.invalid':       'Enter a domain like acme.com',
    'onboarding.domain.confirm':       'Confirm and continue',

    'onboarding.done.title':           "You're all set",
    'onboarding.done.lede':            "Your workspace is ready. We're building your company profile in the background - your agents will get smarter as it lands.",
    'onboarding.done.connect_tools':   'Connect more tools',
    'onboarding.done.go_to_hq':        'Go to HQ',

    // ── web-ui ConsentView.vue ──
    'consent.loading':                 'Loading…',
    'consent.unavailable_title':       'Request unavailable',
    'consent.authorize_title':         'Authorize {name}',
    'consent.lede_after':              'wants to access your HQ account and will be able to:',
    'consent.note':                    "It acts only within your own permissions, never more than what's listed here. You can revoke access any time from your account.",
    'consent.deny':                    'Deny',
    'consent.allow':                   'Allow access',
    'consent.authorizing':             'Authorizing…',
    'consent.err.missing':             'Missing consent request.',
    'consent.err.invalid':             'This consent request is invalid or expired.',
    'consent.err.complete':            'Could not complete the request.',

    // ── web-ui DeviceView.vue (RFC 8628 device-code verification) ──
    'device.loading':                  'Loading…',
    'device.unavailable_title':        'Request unavailable',
    'device.enter_title':              'Enter your code',
    'device.enter_lede':               'Type the code shown in the app you want to sign in.',
    'device.enter_cta':                'Continue',
    'device.signin_title':             'Sign in to approve',
    'device.signin_lede':              "Sign in to HQ to approve this request. This page updates on its own once you're signed in.",
    'device.signin_cta':               'Sign in',
    'device.authorize_title':          'Approve {name}',
    'device.lede_after':               'is asking to sign in and will be able to:',
    'device.note':                     "It acts only within your own permissions, never more than what's listed here. You can revoke access any time from your account.",
    'device.deny':                     'Deny',
    'device.allow':                    'Approve',
    'device.authorizing':              'Approving…',
    'device.approved_title':           "You're all set",
    'device.approved_body':            'Approved. You can return to the app - it is signing you in now.',
    'device.denied_title':             'Request denied',
    'device.denied_body':              'No access was granted. You can close this page.',
    'device.err.invalid':              'This code is invalid, expired, or already used.',
    'device.err.complete':             'Could not complete the request.',

    // ── web-ui GetStartedSlackView.vue ──
    'getStartedSlack.title':           'Add HQ to Slack',
    'getStartedSlack.sub':             'Enter your invite code, then add HQ to your Slack workspace. We use your existing Slack identity - no separate password.',
    'getStartedSlack.invite_ph':       'Invite code',
    'getStartedSlack.invite_aria':     'Invite code',
    'getStartedSlack.checking':        'Checking your code...',
    'getStartedSlack.code_ok':         'Invite code accepted.',
    'getStartedSlack.code_bad':        "That code isn't valid. Check it and try again.",
    'getStartedSlack.add_to_slack':    'Add to Slack',
    'getStartedSlack.add_sub_valid':   'Install HQ into your workspace',
    'getStartedSlack.add_sub_invalid': 'Enter a valid invite code first',
    'getStartedSlack.already_installed':'Already installed?',
    'getStartedSlack.sign_in':         'Sign in',
    'getStartedSlack.no_invite_before':"Don't have an invite?",
    'getStartedSlack.request_access':  'Request access',
    'getStartedSlack.req_name_ph':     'Name',
    'getStartedSlack.req_name_aria':   'Name',
    'getStartedSlack.req_email_ph':    'Work email',
    'getStartedSlack.req_email_aria':  'Work email',
    'getStartedSlack.req_company_ph':  'Company (optional)',
    'getStartedSlack.req_company_aria':'Company',
    'getStartedSlack.req_message_ph':  'What would you like a specialist to do? (optional)',
    'getStartedSlack.req_message_aria':'What would you like a specialist to do?',
    'getStartedSlack.requested_thanks':"Thanks - we'll be in touch at {email}.",
    'getStartedSlack.err.check_code':  'Could not check that code. Try again.',
    'getStartedSlack.err.send_request':'Could not send your request. Please try again.',
    'getStartedSlack.reason.expired':  'That install link expired or was incomplete. Enter your invite code to start again.',
    'getStartedSlack.reason.needs_invite':'Your Slack workspace needs an invite to install HQ. Enter your code below, or request access.',
    'getStartedSlack.reason.bad_invite':'That invite is invalid, expired, or already used. Check it, or request a new one below.',
    'getStartedSlack.reason.cancelled':'Install was cancelled. Enter your invite code whenever you are ready to try again.',
    'getStartedSlack.reason.enterprise':"Enterprise Grid (org-wide) installs aren't supported yet. Install HQ into a single workspace, or request help below.",
    'getStartedSlack.aside.eyebrow':   'Welcome to HQ',
    'getStartedSlack.aside.title':     'Specialists that do the work, right in Slack',
    'getStartedSlack.aside.lede':      '@mention a specialist in Slack and it researches, decides, and delivers - reports, documents, email, schedules.',
    'getStartedSlack.aside.point1':    'Does real work: research, documents, email, schedules',
    'getStartedSlack.aside.point2':    'Lives in Slack, not another tab',
    'getStartedSlack.aside.point3':    'Remembers and learns from your work',
    'getStartedSlack.aside.cta':       'No Slack? Start with email',
    'getStartedSlack.aside.note':      'Invite required during beta.',

    // ── web-ui GetStartedTeamsView.vue (Teams track; reuses getStartedSlack.* + connect.teams_* for the shared bits) ──
    'getStartedTeams.title':              'Add HQ to Microsoft Teams',
    'getStartedTeams.sub':                'Enter your invite code, then sign in with Microsoft to activate HQ for your organization.',
    'getStartedTeams.continue':           'Continue with Microsoft',
    'getStartedTeams.redirecting':        'Redirecting…',
    'getStartedTeams.continue_sub_valid': 'Verify your organization to continue',
    'getStartedTeams.continue_sub_invalid':'Enter a valid invite code first',
    'getStartedTeams.granted_lead':       'Organization verified. Now add HQ to Teams:',
    'getStartedTeams.err_start':          'Could not start Microsoft sign-in. Please try again.',
    'getStartedTeams.reason_error':       'Microsoft sign-in did not complete. Please try again.',

    // ── web-ui SignupView.vue ──
    'signup.title':                    'Create a workspace',
    'signup.sub':                      "Start a new HQ workspace with email - no Slack or Teams needed. You'll get a sign-in link by email to finish.",
    'signup.workspace_aria':           'Workspace name',
    'signup.workspace_ph':             'Workspace name (e.g. Acme)',
    'signup.checking_availability':    'Checking availability...',
    'signup.slug_free_before':         'Your workspace will live at',
    'signup.slug_taken_after':         "is taken - we'll add a suffix to keep it unique.",
    'signup.email_aria':               'Email address',
    'signup.website_aria':             'Company website',
    'signup.website_ph':               'Company website (optional, e.g. acme.com)',
    'signup.invite_aria':              'Invite code',
    'signup.invite_ph':                'Invite code',
    'signup.creating':                 'Creating…',
    'signup.create':                   'Create workspace',
    'signup.create_sub':               "We'll email you a link to finish setup",
    'signup.check_email_title':        'Check your email',
    'signup.check_email_before':       "We've sent a sign-in link to",
    'signup.check_email_mid':          '. Click it to finish setting up',
    'signup.check_email_after':        'and sign in. The link is single-use and expires in 15 minutes.',
    'signup.fallback_link':            "Email delivery isn't configured here - use this link to finish:",
    'signup.err.workspace':            'Enter a workspace name.',
    'signup.err.email':                'Enter a valid email address.',
    'signup.err.invite':               'An invite code is required.',
    'signup.err.create':               'Could not create the workspace.',
    'signup.aside.eyebrow':            'Welcome to HQ',
    'signup.aside.title':              'Your specialists, ready from day one',
    'signup.aside.lede':               'Your new workspace comes with specialists that do real work in Slack, Teams, and the web - research, documents, email, schedules.',
    'signup.aside.point1':             'Does real work, not just chat',
    'signup.aside.point2':             "Add Slack or Teams whenever you're ready",
    'signup.aside.point3':             'Remembers and learns from your work',
    'signup.aside.cta':                'Sign in',
    'signup.aside.note':               'Already have a workspace?',

    // ── web-ui MagicView.vue ──
    'magic.signing_in':                'Signing you in…',
    'magic.verifying':                 'Verifying your sign-in link.',
    'magic.choose_title':              'Choose a workspace',
    'magic.choose_sub':                'This email can sign in to more than one workspace. Pick where to go.',
    'magic.signed_in':                 'Signed in',
    'magic.taking_you':                'Taking you to HQ…',
    'magic.failed_title':              "That link didn't work",
    'magic.back_to_signin':            'Back to sign-in',
    'magic.get_new_link':              'Get a new sign-in link',
    'signin.expired_note':             'That sign-in link expired or was already used. Enter your email below to get a new one.',
    'magic.err.invalid':               'This sign-in link is invalid or has expired. Request a new one from the sign-in page.',
    'magic.err.missing_token':         'This sign-in link is missing its token.',
    'magic.code.title':                'Enter your sign-in code',
    'magic.code.sub':                  'We emailed a code along with the link. Enter it here to finish signing in.',
    'magic.code.aria':                 'Sign-in code',
    'magic.code.cta':                  'Sign in',

    // ── web-ui OAuthCallbackView.vue ──
    'oauthCallback.finishing':         'Finishing setup…',
    'oauthCallback.exchanging':        'Exchanging the authorization code for an access token. This usually takes a moment.',
    'oauthCallback.connected':         'Connected',
    'oauthCallback.redirecting':       'Redirecting to Integrations…',
    'oauthCallback.failed_title':      "We couldn't finish that connection",
    'oauthCallback.back_to_integrations':'Back to Integrations',
    'oauthCallback.err.missing_params':'Missing required code / state parameters. This usually means the provider redirect URL is misconfigured.',
    'oauthCallback.err.exchange_failed':'Token exchange failed. The link may have expired - go back to Integrations and click Reconnect to try again.',

    // ── web-ui NotFoundView.vue ──
    'notFound.body':                   "That page doesn't exist (yet).",
    'notFound.back_home':              'Back home',

    // ── web-ui HelpView.vue ──
    'help.title':                      'Help & docs',
    'help.subtitle':                   'How HQ works, answers to common questions, and where to find the developer docs.',
    'help.new_article':                'New article',
    'help.cat.getting_started':        'Getting started',
    'help.cat.faq':                    'Frequently asked',
    'help.cat.general':                'General',
    'help.editor.slug':                'Slug',
    'help.editor.slug_ph':             'my-article',
    'help.editor.title':               'Title',
    'help.editor.category':            'Category',
    'help.editor.order':               'Order',
    'help.editor.summary':             'Summary',
    'help.editor.summary_ph':          'One line shown in the browse list + search',
    'help.editor.markdown':            'Markdown',
    'help.editor.preview':             'Preview',
    'help.search_ph':                  'Search help - ask a question or a keyword',
    'help.search_aria':                'Search help',
    'help.no_results':                 'No results for "{query}". Try different words, or browse the topics below.',
    'help.all_help':                   'All help',
    'help.article_missing':            "That article doesn't exist.",
    'help.back_to_help':               'Back to help',
    'help.devdocs_title':              'Developer & API docs',
    'help.devdocs_sub':                'Build on HQ - API reference, guides, and SDKs at docs.hq.zone',
    'help.content_preparing':          'Help content is being prepared.',
    'help.saved':                      'Saved.',
    'help.deleted':                    'Deleted.',
    'help.delete.title':               'Delete this article?',
    'help.delete.body':                '"{title}" will be removed from Help and its search index. This cannot be undone.',
    'help.delete.confirm':             'Delete',
    'help.err.title_body_required':    'Title and body are required.',
    'help.err.slug_format':            'Slug must be lowercase letters, numbers, and hyphens.',
    'help.err.save_failed':            'Failed to save.',
    'help.err.delete_failed':          'Failed to delete.',
  },
  sv: {
    // ── web-ui OnboardingView.vue + OnboardingStepper.vue ──
    'onboarding.stepper_aria':         'Introduktionsförlopp',
    'onboarding.step.welcome':         'Välkommen',
    'onboarding.step.url':             'Arbetsyte-URL',
    'onboarding.step.channels':        'Kanaler',
    'onboarding.step.domain':          'Domän',
    'onboarding.step.done':            'Klart',
    'onboarding.switch_to':            'Byt till {name}',
    'onboarding.theme_light':          'Ljust',
    'onboarding.theme_dark':           'Mörkt',
    'onboarding.theme_light_title':    'Ljust tema',
    'onboarding.theme_dark_title':     'Mörkt tema',
    'onboarding.sign_out':             'Logga ut',
    'onboarding.loading_workspace':    'Läser in din arbetsyta...',
    'onboarding.continue':             'Fortsätt',
    'onboarding.err.join_channel':     'Det gick inte att lägga till HQ i en kanal.',

    'onboarding.welcome.title':        'Välkommen till HQ',
    'onboarding.welcome.lede':         'Nu sätter vi upp din arbetsyta. Det tar bara en minut - vi ansluter några kanaler, lär oss om ditt företag, och sedan är du redo att sätta dina agenter i arbete.',
    'onboarding.welcome.cta':          'Kom igång',

    'onboarding.url.title':            'Välj din arbetsyte-URL',
    'onboarding.url.lede':             'Det här är arbetsytans handtag - det finns i din webbadress och i dina agenters e-postadress. Vi valde ett utifrån ditt arbetsytenamn; ändra det om du vill. Du kan byta namn senare i inställningarna.',

    'onboarding.channels.title':       'Lägg till HQ i dina kanaler',
    'onboarding.channels.lede':        'Välj kanalerna där HQ ska lyssna och hjälpa till. Du kan alltid lägga till fler senare i arbetsyteinställningarna.',
    'onboarding.channels.loading':     'Läser in kanaler...',
    'onboarding.channels.connected':   'Ansluten',
    'onboarding.channels.add':         'Lägg till kanaler',
    'onboarding.channels.empty_title': 'Inga kanaler att lägga till',
    'onboarding.channels.empty_body':  'HQ är redan med i varje offentlig kanal det kan ansluta sig till på egen hand.',
    'onboarding.channels.private_before':'Privata kanaler kräver en inbjudan - kör',
    'onboarding.channels.private_after': 'i Slack.',
    'onboarding.channels.skip':        'Hoppa över för nu',
    'onboarding.channels.join_n':      'Anslut {count} valda',

    'onboarding.domain.title':         'Bekräfta din företagsdomän',
    'onboarding.domain.lede':          'Bekräfta din företagsdomän så att vi kan bygga din arbetsyteprofil. Vi använder den för att lära oss vad ditt företag gör och skräddarsy dina agenter.',
    'onboarding.domain.aria':          'Företagsdomän',
    'onboarding.domain.checking':      'Kontrollerar...',
    'onboarding.domain.ok_after':      'ser bra ut',
    'onboarding.domain.unresolved':    'Vi kunde inte hitta den domänen - kontrollera stavningen',
    'onboarding.domain.invalid':       'Ange en domän som acme.com',
    'onboarding.domain.confirm':       'Bekräfta och fortsätt',

    'onboarding.done.title':           'Allt är klart',
    'onboarding.done.lede':            'Din arbetsyta är redo. Vi bygger din företagsprofil i bakgrunden - dina agenter blir smartare allteftersom den landar.',
    'onboarding.done.connect_tools':   'Anslut fler verktyg',
    'onboarding.done.go_to_hq':        'Gå till HQ',

    // ── web-ui ConsentView.vue ──
    'consent.loading':                 'Läser in…',
    'consent.unavailable_title':       'Begäran är inte tillgänglig',
    'consent.authorize_title':         'Auktorisera {name}',
    'consent.lede_after':              'vill få åtkomst till ditt HQ-konto och kommer att kunna:',
    'consent.note':                    'Den agerar bara inom dina egna behörigheter, aldrig mer än det som listas här. Du kan återkalla åtkomsten när som helst från ditt konto.',
    'consent.deny':                    'Neka',
    'consent.allow':                   'Tillåt åtkomst',
    'consent.authorizing':             'Auktoriserar…',
    'consent.err.missing':             'Begäran om samtycke saknas.',
    'consent.err.invalid':             'Den här begäran om samtycke är ogiltig eller har gått ut.',
    'consent.err.complete':            'Det gick inte att slutföra begäran.',

    // ── web-ui DeviceView.vue (RFC 8628 enhetskodsverifiering) ──
    'device.loading':                  'Läser in…',
    'device.unavailable_title':        'Begäran är inte tillgänglig',
    'device.enter_title':              'Ange din kod',
    'device.enter_lede':               'Skriv koden som visas i appen du vill logga in.',
    'device.enter_cta':                'Fortsätt',
    'device.signin_title':             'Logga in för att godkänna',
    'device.signin_lede':              'Logga in på HQ för att godkänna den här begäran. Sidan uppdateras automatiskt när du är inloggad.',
    'device.signin_cta':               'Logga in',
    'device.authorize_title':          'Godkänn {name}',
    'device.lede_after':               'begär att logga in och kommer att kunna:',
    'device.note':                     'Den agerar bara inom dina egna behörigheter, aldrig mer än det som listas här. Du kan återkalla åtkomsten när som helst från ditt konto.',
    'device.deny':                     'Neka',
    'device.allow':                    'Godkänn',
    'device.authorizing':              'Godkänner…',
    'device.approved_title':           'Allt klart',
    'device.approved_body':            'Godkänd. Du kan återgå till appen – den loggar in dig nu.',
    'device.denied_title':             'Begäran nekad',
    'device.denied_body':              'Ingen åtkomst beviljades. Du kan stänga den här sidan.',
    'device.err.invalid':              'Koden är ogiltig, har gått ut eller har redan använts.',
    'device.err.complete':             'Det gick inte att slutföra begäran.',

    // ── web-ui GetStartedSlackView.vue ──
    'getStartedSlack.title':           'Lägg till HQ i Slack',
    'getStartedSlack.sub':             'Ange din inbjudningskod och lägg sedan till HQ i din Slack-arbetsyta. Vi använder din befintliga Slack-identitet - inget separat lösenord.',
    'getStartedSlack.invite_ph':       'Inbjudningskod',
    'getStartedSlack.invite_aria':     'Inbjudningskod',
    'getStartedSlack.checking':        'Kontrollerar din kod...',
    'getStartedSlack.code_ok':         'Inbjudningskoden godkänd.',
    'getStartedSlack.code_bad':        'Den koden är inte giltig. Kontrollera den och försök igen.',
    'getStartedSlack.add_to_slack':    'Lägg till i Slack',
    'getStartedSlack.add_sub_valid':   'Installera HQ i din arbetsyta',
    'getStartedSlack.add_sub_invalid': 'Ange en giltig inbjudningskod först',
    'getStartedSlack.already_installed':'Redan installerat?',
    'getStartedSlack.sign_in':         'Logga in',
    'getStartedSlack.no_invite_before':'Har du ingen inbjudan?',
    'getStartedSlack.request_access':  'Begär åtkomst',
    'getStartedSlack.req_name_ph':     'Namn',
    'getStartedSlack.req_name_aria':   'Namn',
    'getStartedSlack.req_email_ph':    'Jobbe-post',
    'getStartedSlack.req_email_aria':  'Jobbe-post',
    'getStartedSlack.req_company_ph':  'Företag (valfritt)',
    'getStartedSlack.req_company_aria':'Företag',
    'getStartedSlack.req_message_ph':  'Vad vill du att en specialist ska göra? (valfritt)',
    'getStartedSlack.req_message_aria':'Vad vill du att en specialist ska göra?',
    'getStartedSlack.requested_thanks':'Tack - vi hör av oss till {email}.',
    'getStartedSlack.err.check_code':  'Det gick inte att kontrollera koden. Försök igen.',
    'getStartedSlack.err.send_request':'Det gick inte att skicka din begäran. Försök igen.',
    'getStartedSlack.reason.expired':  'Den installationslänken gick ut eller var ofullständig. Ange din inbjudningskod för att börja om.',
    'getStartedSlack.reason.needs_invite':'Din Slack-arbetsyta behöver en inbjudan för att installera HQ. Ange din kod nedan, eller begär åtkomst.',
    'getStartedSlack.reason.bad_invite':'Den inbjudan är ogiltig, har gått ut eller är redan använd. Kontrollera den, eller begär en ny nedan.',
    'getStartedSlack.reason.cancelled':'Installationen avbröts. Ange din inbjudningskod när du är redo att försöka igen.',
    'getStartedSlack.reason.enterprise':'Installationer för Enterprise Grid (org-omfattande) stöds inte än. Installera HQ i en enskild arbetsyta, eller begär hjälp nedan.',
    'getStartedSlack.aside.eyebrow':   'Välkommen till HQ',
    'getStartedSlack.aside.title':     'Specialister som gör jobbet, direkt i Slack',
    'getStartedSlack.aside.lede':      '@nämn en specialist i Slack så gör den research, beslutar och levererar - rapporter, dokument, e-post, scheman.',
    'getStartedSlack.aside.point1':    'Gör riktigt arbete: research, dokument, e-post, scheman',
    'getStartedSlack.aside.point2':    'Lever i Slack, inte ännu en flik',
    'getStartedSlack.aside.point3':    'Kommer ihåg och lär av arbetet',
    'getStartedSlack.aside.cta':       'Ingen Slack? Börja med e-post',
    'getStartedSlack.aside.note':      'Inbjudan krävs under betan.',

    // ── web-ui GetStartedTeamsView.vue ──
    'getStartedTeams.title':              'Lägg till HQ i Microsoft Teams',
    'getStartedTeams.sub':                'Ange din inbjudningskod och logga sedan in med Microsoft för att aktivera HQ för din organisation.',
    'getStartedTeams.continue':           'Fortsätt med Microsoft',
    'getStartedTeams.redirecting':        'Omdirigerar…',
    'getStartedTeams.continue_sub_valid': 'Verifiera din organisation för att fortsätta',
    'getStartedTeams.continue_sub_invalid':'Ange en giltig inbjudningskod först',
    'getStartedTeams.granted_lead':       'Organisationen verifierad. Lägg nu till HQ i Teams:',
    'getStartedTeams.err_start':          'Det gick inte att starta Microsoft-inloggningen. Försök igen.',
    'getStartedTeams.reason_error':       'Microsoft-inloggningen slutfördes inte. Försök igen.',

    // ── web-ui SignupView.vue ──
    'signup.title':                    'Skapa en arbetsyta',
    'signup.sub':                      'Starta en ny HQ-arbetsyta med e-post - ingen Slack eller Teams behövs. Du får en inloggningslänk via e-post för att slutföra.',
    'signup.workspace_aria':           'Arbetsytenamn',
    'signup.workspace_ph':             'Arbetsytenamn (t.ex. Acme)',
    'signup.checking_availability':    'Kontrollerar tillgänglighet...',
    'signup.slug_free_before':         'Din arbetsyta kommer att finnas på',
    'signup.slug_taken_after':         'är upptaget - vi lägger till ett suffix för att hålla det unikt.',
    'signup.email_aria':               'E-postadress',
    'signup.website_aria':             'Företagets webbplats',
    'signup.website_ph':               'Företagets webbplats (valfritt, t.ex. acme.com)',
    'signup.invite_aria':              'Inbjudningskod',
    'signup.invite_ph':                'Inbjudningskod',
    'signup.creating':                 'Skapar…',
    'signup.create':                   'Skapa arbetsyta',
    'signup.create_sub':               'Vi mejlar dig en länk för att slutföra uppsättningen',
    'signup.check_email_title':        'Kolla din e-post',
    'signup.check_email_before':       'Vi har skickat en inloggningslänk till',
    'signup.check_email_mid':          '. Klicka på den för att slutföra uppsättningen av',
    'signup.check_email_after':        'och logga in. Länken är engångsbruk och går ut om 15 minuter.',
    'signup.fallback_link':            'E-postleverans är inte konfigurerad här - använd den här länken för att slutföra:',
    'signup.err.workspace':            'Ange ett arbetsytenamn.',
    'signup.err.email':                'Ange en giltig e-postadress.',
    'signup.err.invite':               'En inbjudningskod krävs.',
    'signup.err.create':               'Det gick inte att skapa arbetsytan.',
    'signup.aside.eyebrow':            'Välkommen till HQ',
    'signup.aside.title':              'Dina specialister, redo från dag ett',
    'signup.aside.lede':               'Din nya arbetsyta kommer med specialister som gör riktigt arbete i Slack, Teams och webben - research, dokument, e-post, scheman.',
    'signup.aside.point1':             'Gör riktigt arbete, inte bara chattar',
    'signup.aside.point2':             'Lägg till Slack eller Teams när du är redo',
    'signup.aside.point3':             'Kommer ihåg och lär av arbetet',
    'signup.aside.cta':                'Logga in',
    'signup.aside.note':               'Har du redan en arbetsyta?',

    // ── web-ui MagicView.vue ──
    'magic.signing_in':                'Loggar in dig…',
    'magic.verifying':                 'Verifierar din inloggningslänk.',
    'magic.choose_title':              'Välj en arbetsyta',
    'magic.choose_sub':                'Den här e-posten kan logga in på fler än en arbetsyta. Välj vart du vill gå.',
    'magic.signed_in':                 'Inloggad',
    'magic.taking_you':                'Tar dig till HQ…',
    'magic.failed_title':              'Den länken fungerade inte',
    'magic.back_to_signin':            'Tillbaka till inloggning',
    'magic.get_new_link':              'Hämta en ny inloggningslänk',
    'signin.expired_note':             'Den inloggningslänken har gått ut eller är redan använd. Ange din e-postadress nedan för att få en ny.',
    'magic.err.invalid':               'Den här inloggningslänken är ogiltig eller har gått ut. Begär en ny från inloggningssidan.',
    'magic.err.missing_token':         'Den här inloggningslänken saknar sin token.',
    'magic.code.title':                'Ange din inloggningskod',
    'magic.code.sub':                  'Vi mejlade en kod tillsammans med länken. Ange den här för att slutföra inloggningen.',
    'magic.code.aria':                 'Inloggningskod',
    'magic.code.cta':                  'Logga in',

    // ── web-ui OAuthCallbackView.vue ──
    'oauthCallback.finishing':         'Slutför uppsättningen…',
    'oauthCallback.exchanging':        'Byter auktoriseringskoden mot en åtkomsttoken. Det här tar oftast ett ögonblick.',
    'oauthCallback.connected':         'Ansluten',
    'oauthCallback.redirecting':       'Omdirigerar till Integrationer…',
    'oauthCallback.failed_title':      'Vi kunde inte slutföra den anslutningen',
    'oauthCallback.back_to_integrations':'Tillbaka till Integrationer',
    'oauthCallback.err.missing_params':'Obligatoriska kod-/state-parametrar saknas. Det här betyder oftast att leverantörens omdirigerings-URL är felkonfigurerad.',
    'oauthCallback.err.exchange_failed':'Tokenbytet misslyckades. Länken kan ha gått ut - gå tillbaka till Integrationer och klicka på Återanslut för att försöka igen.',

    // ── web-ui NotFoundView.vue ──
    'notFound.body':                   'Den sidan finns inte (än).',
    'notFound.back_home':              'Hem igen',

    // ── web-ui HelpView.vue ──
    'help.title':                      'Hjälp och dokument',
    'help.subtitle':                   'Hur HQ fungerar, svar på vanliga frågor, och var du hittar utvecklardokumenten.',
    'help.new_article':                'Ny artikel',
    'help.cat.getting_started':        'Kom igång',
    'help.cat.faq':                    'Vanliga frågor',
    'help.cat.general':                'Allmänt',
    'help.editor.slug':                'Slug',
    'help.editor.slug_ph':             'min-artikel',
    'help.editor.title':               'Titel',
    'help.editor.category':            'Kategori',
    'help.editor.order':               'Ordning',
    'help.editor.summary':             'Sammanfattning',
    'help.editor.summary_ph':          'En rad som visas i bläddringslistan + sökningen',
    'help.editor.markdown':            'Markdown',
    'help.editor.preview':             'Förhandsvisning',
    'help.search_ph':                  'Sök i hjälpen - ställ en fråga eller ett nyckelord',
    'help.search_aria':                'Sök i hjälpen',
    'help.no_results':                 'Inga resultat för "{query}". Prova andra ord, eller bläddra bland ämnena nedan.',
    'help.all_help':                   'All hjälp',
    'help.article_missing':            'Den artikeln finns inte.',
    'help.back_to_help':               'Tillbaka till hjälpen',
    'help.devdocs_title':              'Utvecklar- och API-dokument',
    'help.devdocs_sub':                'Bygg på HQ - API-referens, guider och SDK:er på docs.hq.zone',
    'help.content_preparing':          'Hjälpinnehållet förbereds.',
    'help.saved':                      'Sparat.',
    'help.deleted':                    'Borttaget.',
    'help.delete.title':               'Ta bort den här artikeln?',
    'help.delete.body':                '"{title}" tas bort från Hjälp och dess sökindex. Detta kan inte ångras.',
    'help.delete.confirm':             'Ta bort',
    'help.err.title_body_required':    'Titel och brödtext krävs.',
    'help.err.slug_format':            'Slug måste bestå av små bokstäver, siffror och bindestreck.',
    'help.err.save_failed':            'Det gick inte att spara.',
    'help.err.delete_failed':          'Det gick inte att ta bort.',
  },
};
